// frontend/src/pages/AccountingTypes/AccountingTypeList.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Building,
  CheckCircle,
  Filter,
  Layers,
  Loader2,
  Lock,
  RefreshCw,
  Search,
} from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";

import { useAuth } from "../../contexts/AuthContext";
import "./AccountingTypeList.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const MAIN_TYPES = ["Asset", "Liability", "Equity", "Income", "Expense"];

const canonicalMainType = (value) => {
  if (value === "Capital") return "Equity";
  if (value === "Revenue") return "Income";
  return value || "";
};

const getId = (value) => {
  if (!value) return "";
  if (typeof value === "object") return value._id || value.id || "";
  return value;
};

const AccountingTypeList = () => {
  const { currentUser, userType, company } = useAuth();

  const isSystemAdmin =
    userType === "system" &&
    ["developer", "admin"].includes(currentUser?.role);

  const contextCompanyId =
    userType === "company"
      ? getId(company) || getId(currentUser?.company_id)
      : "";

  const [records, setRecords] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(contextCompanyId);

  useEffect(() => {
    if (isSystemAdmin) return;
    setSelectedCompany(contextCompanyId);
  }, [contextCompanyId, isSystemAdmin]);

  useEffect(() => {
    if (!isSystemAdmin) return;

    let active = true;

    const fetchCompanies = async () => {
      try {
        const response = await axios.get(`${url}/api/company`, {
          headers: getAuthHeaders(),
        });
        if (!active) return;

        const nextCompanies = response.data?.data || [];
        setCompanies(nextCompanies);
        setSelectedCompany((current) => current || nextCompanies[0]?._id || "");
      } catch (error) {
        if (!active) return;
        toast.error(
          error.response?.data?.message || "Failed to load companies",
        );
        setLoading(false);
      }
    };

    fetchCompanies();

    return () => {
      active = false;
    };
  }, [isSystemAdmin]);

  const fetchRecords = useCallback(async () => {
    if (!selectedCompany) {
      setRecords([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Viewing the list must be read-only and fast. The repair endpoint is
      // intentionally not called here; use the explicit Repair Setup action.
      const response = await axios.get(`${url}/api/accounting-types`, {
        params: {
          company_id: selectedCompany,
          status: "active",
          limit: 100,
        },
        headers: getAuthHeaders(),
      });

      setRecords(response.data?.data || []);
    } catch (error) {
      setRecords([]);
      toast.error(
        error.response?.data?.message || "Failed to load accounting types",
      );
    } finally {
      setLoading(false);
    }
  }, [selectedCompany]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const filtered = useMemo(() => {
    const value = search.trim().toLowerCase();

    return records.filter((item) => {
      const mainType = canonicalMainType(item.type);

      if (selectedType && mainType !== selectedType) return false;
      if (!value) return true;

      return [
        item.type_code,
        mainType,
        item.type,
        item.sub_type,
        item.normal_balance,
        item.description,
      ].some((field) => String(field || "").toLowerCase().includes(value));
    });
  }, [records, search, selectedType]);

  const stats = useMemo(
    () => ({
      total: records.length,
      active: records.filter((item) => item.status === "active").length,
      debit: records.filter((item) => item.normal_balance === "DR").length,
      credit: records.filter((item) => item.normal_balance === "CR").length,
    }),
    [records],
  );

  if (loading) {
    return (
      <div className="accounting-list-loading">
        <Loader2 className="animate-spin" size={35} />
        <p>Loading fixed accounting types…</p>
      </div>
    );
  }

  return (
    <div className="accounting-list-page">
      <header className="accounting-list-header">
        <div>
          <h1>
            <Layers size={25} /> Accounting Types
          </h1>
          <p>
            Eight protected classifications for the retail, wholesale and
            inventory ledger.
          </p>
        </div>

        <div className="accounting-list-actions">
          <span className="record-status active">
            <Lock size={14} /> System managed
          </span>
          <button
            type="button"
            className="list-filter-btn"
            onClick={() => setShowFilters((previous) => !previous)}
          >
            <Filter size={17} /> Filters
          </button>
        </div>
      </header>

      <section className="accounting-list-stats">
        <div>
          <span>Total Types</span>
          <strong>{stats.total}</strong>
        </div>
        <div>
          <span>Active</span>
          <strong>{stats.active}</strong>
        </div>
        <div>
          <span>Debit Normal</span>
          <strong>{stats.debit}</strong>
        </div>
        <div>
          <span>Credit Normal</span>
          <strong>{stats.credit}</strong>
        </div>
      </section>

      {showFilters && (
        <section className="accounting-list-filters">
          {isSystemAdmin && (
            <label>
              Company
              <select
                value={selectedCompany}
                onChange={(event) => setSelectedCompany(event.target.value)}
              >
                {companies.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.company_name}
                    {item.company_code ? ` (${item.company_code})` : ""}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label>
            Main Type
            <select
              value={selectedType}
              onChange={(event) => setSelectedType(event.target.value)}
            >
              <option value="">All types</option>
              {MAIN_TYPES.map((item) => (
                <option value={item} key={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            className="list-refresh-btn"
            onClick={fetchRecords}
          >
            <RefreshCw size={17} /> Refresh
          </button>
        </section>
      )}

      <section className="accounting-list-search">
        <Search size={18} />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search code, main type, sub type or description…"
        />
      </section>

      <section className="accounting-list-table-wrap">
        <table className="accounting-list-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Type Code</th>
              <th>Main Type</th>
              <th>Sub Type</th>
              <th>Normal Balance</th>
              <th>Financial Statement</th>
              {isSystemAdmin && <th>Company</th>}
              <th>Status</th>
              <th>Protection</th>
            </tr>
          </thead>

          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={isSystemAdmin ? 9 : 8}
                  className="accounting-empty-cell"
                >
                  No accounting types found.
                </td>
              </tr>
            ) : (
              filtered.map((item, index) => {
                const mainType = canonicalMainType(item.type);

                return (
                  <tr key={item._id}>
                    <td>{index + 1}</td>
                    <td>
                      <code>{item.type_code}</code>
                    </td>
                    <td>
                      <span
                        className={`main-type-badge ${mainType.toLowerCase()}`}
                      >
                        {mainType}
                      </span>
                    </td>
                    <td>{item.sub_type}</td>
                    <td>
                      <span
                        className={`normal-balance-badge ${item.normal_balance.toLowerCase()}`}
                      >
                        {item.normal_balance}
                      </span>
                    </td>
                    <td>
                      {item.financial_statement === "balance_sheet"
                        ? "Balance Sheet"
                        : "Income Statement"}
                    </td>
                    {isSystemAdmin && (
                      <td>
                        <span className="company-cell">
                          <Building size={14} />
                          {item.company?.company_name ||
                            item.company_id?.company_name ||
                            "-"}
                        </span>
                      </td>
                    )}
                    <td>
                      <span className="record-status active">
                        <CheckCircle size={13} /> Active
                      </span>
                    </td>
                    <td>
                      <span className="record-status active">
                        <Lock size={13} /> Locked
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default AccountingTypeList;